import {IBrand, ISegment} from '../../types/Filter';
import AxiosService from '../AxiosService';

// HTTP/1.1 headers must be ASCII-safe. Strip combining diacritical marks after
// NFD decomposition so accented chars like Á → A survive Latin-1 decoding on
// the server without losing bytes (e.g. "Água Crystal" → "Agua Crystal").
const toAsciiHeader = (value?: string): string | undefined =>
  value?.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const catalogProductsGet = async (
  clientCode: string | undefined,
  perPage: number,
  currentPage: number,
  category?: string,
  brand?: string,
  search?: string,
  sort?: string,
  skus?: string,
  packaging?: string,
  onlyPromotions?: boolean,
): Promise<ICatalogProducts> => {
  const instance = AxiosService.createProductsAxiosInstance();

  const response = await instance.get('/catalog/products', {
    headers: {
      'client-code': clientCode,
      'per-page': perPage,
      'current-page': currentPage,
      category: toAsciiHeader(category),
      brand: toAsciiHeader(brand),
      search: search,
      sort: sort,
      skus: skus,
      packaging: packaging,
      ...(onlyPromotions ? {'only-promotions': 'true'} : {}),
    },
  });

  return response.data;
};

const updatedCatalogProductsGet = async (
  clientCode: string | undefined,
  perPage: number,
  currentPage: number,
  category?: string,
  brand?: string,
  search?: string,
  sort?: string,
  packaging?: string,
): Promise<ICatalogProducts> => {
  const instance = AxiosService.createProductsAxiosInstance();

  const response = await instance.get('/catalog/updated-products', {
    headers: {
      'client-code': clientCode,
      'per-page': perPage,
      'current-page': currentPage,
      category: toAsciiHeader(category),
      brand: toAsciiHeader(brand),
      search: search,
      sort: sort,
      packaging: packaging,
    },
  });

  return response.data;
};

const clientLimitsGet = async (clientCode: string | undefined) => {
  const instance = AxiosService.createProductsAxiosInstance();

  const response = await instance.get('/client/limits', {
    headers: {
      'client-code': clientCode,
    },
  });

  return response.data;
};

const catalogNewsGet = async (
  clientCode: string | undefined,
  perPage: number,
  currentPage: number,
) => {
  const instance = AxiosService.createProductsAxiosInstance();

  const response = await instance.get('/catalog/news', {
    headers: {
      'client-code': clientCode,
      'per-page': perPage,
      'current-page': currentPage,
    },
  });

  return response.data;
};

const featuredProductsGet = async (
  clientCode: string | undefined,
  perPage: number = 30,
  currentPage: number = 1,
) => {
  const instance = AxiosService.createProductsAxiosInstance();

  const response = await instance.get('/catalog/featured', {
    headers: {
      'client-code': clientCode,
      'per-page': perPage,
      'current-page': currentPage,
    },
  });

  return response.data;
};

const trackFeaturedProductView = async (
  clientCode: string | undefined,
  featuredProductId: number,
) => {
  const instance = AxiosService.createProductsAxiosInstance();

  try {
    const response = await instance.post(`/catalog/featured/${featuredProductId}/view`, {}, {
      headers: {
        'client-code': clientCode,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error tracking featured product view:', error);
    // Não lança erro para não interromper o fluxo
    return null;
  }
};

const trackCheckoutViews = async (
  clientCode: string | undefined,
  featuredProductIds?: number[], // Opcional: se não enviado, backend busca todos
) => {
  const instance = AxiosService.createProductsAxiosInstance();

  try {
    const response = await instance.post('/catalog/featured/track-checkout-views',
      featuredProductIds ? { featured_product_ids: featuredProductIds } : {},
      {
        headers: {
          'client-code': clientCode,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error tracking checkout views:', error);
    // Não lança erro para não interromper o fluxo do checkout
    return null;
  }
};

const GetBrands = async (): Promise<IBrand[]> => {
  const instance = AxiosService.createProductsAxiosInstance();
  const response = await instance.get('/images/brands');
  return response.data;
};

const GetSegments = async (clientCode: string): Promise<ISegment[]> => {
  const instance = AxiosService.createProductsAxiosInstance();
  const response = await instance.get('/images/segments', {
    headers: {
      'client-code': clientCode,
  }});
  return response.data;
};

const GetBrandsId = async (id: string): Promise<IBrand[]> => {
  const instance = AxiosService.createProductsAxiosInstance();
  const response = await instance.get(`/images/brands/${id}`);
  return response.data;
};

const GetSegmentsId = async (id: string): Promise<ISegment[]> => {
  const instance = AxiosService.createProductsAxiosInstance();
  const response = await instance.get(`/images/segments/${id}`);
  return response.data;
};

export {clientLimitsGet};

export default {
  catalogProductsGet,
  GetBrands,
  GetSegments,
  catalogNewsGet,
  featuredProductsGet,
  trackFeaturedProductView,
  trackCheckoutViews,
  GetBrandsId,
  GetSegmentsId,
  updatedCatalogProductsGet,
};
